import React from "react";
import {Box, Skeleton, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";

// One tile of the metrics grid: icon next to label / value / note (+ action on the right), then a chart
// spanning the full card width. chartOverlay is shown over the chart on hover (always on touch screens).
export const MetricCard = ({icon, color, label, value, note, chart, chartOverlay, action, loading = false}) => (
  // No height: 100% - the card is the grid item and stretches to its row already; the percentage height
  // let the rows collapse below the card content, so wrapped rows overlapped
  <Box sx={{
    display: "flex",
    flexDirection: "column",
    bgcolor: "#fff",
    border: "1px solid #ECECF3",
    borderRadius: "18px",
    px: 2,
    pt: 2,
    pb: 1,
    boxShadow: "0 1px 2px rgba(35, 38, 77, 0.04)",
    transition: "box-shadow 0.2s",
    "&:hover": {boxShadow: "0 10px 30px rgba(35, 38, 77, 0.08)"},
  }}>
    <Stack direction={"row"} alignItems={"flex-start"} gap={1.5}>
      <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        width: 45, height: 45, borderRadius: "12px",
        color, bgcolor: `${color}1F`,
      }}>
        <HugeiconsIcon icon={icon} size={20}/>
      </Box>
      <Box sx={{minWidth: 0, flex: 1}}>
        <Typography noWrap sx={{fontSize: 12, fontWeight: 500, color: "#6B6B80", lineHeight: 1.3}}>{label}</Typography>
        {loading
          ? <Skeleton variant={"text"} width={64} sx={{fontSize: 24}}/>
          : (
            <Typography component={"strong"} sx={{
              display: "block", fontSize: 24, fontWeight: 700, lineHeight: 1.2,
              letterSpacing: "-0.02em", color: "#23264D",
            }}>
              {value}
            </Typography>
          )}
        <Typography component={"span"} noWrap sx={{
          display: "flex", alignItems: "center", gap: 0.75, mt: 0,
          fontSize: 10, color: "#8A8AA0",
        }}>
          {note}
        </Typography>
      </Box>
      {action && <Box sx={{flexShrink: 0, mt: -0.5, mr: -0.5}}>{action}</Box>}
    </Stack>

    {/* mx cancels the card padding so the chart spans the full card width */}
    <Box sx={{
      position: "relative", mt: "auto", pt: 1.5, mx: -2,
      "& .chart-overlay": {opacity: 0, transition: "opacity 0.15s"},
      "&:hover .chart-overlay, & .chart-overlay:focus-within": {opacity: 1},
      "@media (hover: none)": {"& .chart-overlay": {opacity: 1}},
    }}>
      {chart}
      {chartOverlay && <Box className={"chart-overlay"}>{chartOverlay}</Box>}
    </Box>
  </Box>
);

// Pulsing green dot for live values
export const LiveDot = () => (
  <Box component={"span"} sx={{
    width: 7, height: 7, borderRadius: "50%", bgcolor: "#22C55E", flexShrink: 0,
    boxShadow: "0 0 0 0 rgba(34, 197, 94, 0.5)",
    animation: "metric-live-pulse 1.8s infinite",
    "@keyframes metric-live-pulse": {
      "0%": {boxShadow: "0 0 0 0 rgba(34, 197, 94, 0.5)"},
      "70%": {boxShadow: "0 0 0 6px rgba(34, 197, 94, 0)"},
      "100%": {boxShadow: "0 0 0 0 rgba(34, 197, 94, 0)"},
    },
  }}/>
);

export const NoChartData = ({height = 64}) => (
  <Typography sx={{fontSize: 12, color: "#B4B4C3", textAlign: "center", lineHeight: `${height}px`}}>
    No data
  </Typography>
);
